import { useMemo, useState } from 'react';
import { ChartSuggestion, suggestCharts } from '../../lib/suggestCharts';
import BarChart from '../charts/BarChart';
import Donut from '../charts/Donut';

/**
 * Regroupement fonctionnel des suggestions (vue utilisateur) :
 *  - SURFACES   : surfaces par logement / lot / étage
 *  - COÛTS      : coûts, budget, dépenses
 *  - PLANNING   : phases, jalons, dates
 *  - BIM        : KPIs BIM, indicateurs métier
 *  - PROBLÈMES  : alertes, écarts, problèmes qualité
 */
type Bucket = 'SURFACES' | 'COÛTS' | 'PLANNING' | 'BIM' | 'PROBLÈMES' | 'AUTRES';

const BUCKET_META: Record<Bucket, { label: string; icon: string; hint: string }> = {
  SURFACES: { label: 'Surfaces', icon: '📐', hint: 'Métrés, m², répartitions spatiales' },
  'COÛTS': { label: 'Coûts', icon: '💰', hint: 'CHF, dépenses, budgets, écarts financiers' },
  PLANNING: { label: 'Planning', icon: '📅', hint: 'Phases, jalons, avancement' },
  BIM: { label: 'BIM', icon: '🏗️', hint: 'KPIs BIM, qualité modèle, LOD, ISO 19650' },
  'PROBLÈMES': { label: 'Problèmes', icon: '⚠️', hint: 'Alertes, écarts, points de vigilance' },
  AUTRES: { label: 'Autres', icon: '📊', hint: 'Autres indicateurs' },
};

const BUCKET_ORDER: Bucket[] = ['SURFACES', 'COÛTS', 'PLANNING', 'BIM', 'PROBLÈMES', 'AUTRES'];

function bucketize(s: ChartSuggestion): Bucket {
  const title = (s.title + ' ' + s.description).toLowerCase();
  if (s.category === 'surfaces' || /\bsurface|m²|métré|logement/.test(title)) return 'SURFACES';
  if (s.category === 'costs' || /coût|cout|chf|budget|dépense|depense|€/.test(title)) return 'COÛTS';
  if (s.category === 'schedule' || /planning|phase|jalon|sia|délai|delai|avancement|date/.test(title))
    return 'PLANNING';
  if (/bim|lod|ids|loin|iso 19650|kpi/.test(title)) return 'BIM';
  if (/écart|ecart|retard|problème|probleme|alerte|vigilance|risque/.test(title)) return 'PROBLÈMES';
  return 'AUTRES';
}

const KIND_LABELS: Record<ChartSuggestion['kind'], string> = {
  donut: 'Camembert',
  bar: 'Barres',
  line: 'Courbe',
  radar: 'Radar',
  sankey: 'Flux Sankey',
  timeline: 'Timeline',
  table: 'Tableau',
};

export default function ChartSuggestions({
  extractions,
  onAdd,
}: {
  extractions: any[];
  onAdd: (suggestion: ChartSuggestion) => void;
}) {
  const suggestions = useMemo(() => suggestCharts(extractions), [extractions]);

  // Regroupe par bucket utilisateur
  const grouped = useMemo(() => {
    const m = new Map<Bucket, ChartSuggestion[]>();
    for (const s of suggestions) {
      const b = bucketize(s);
      if (!m.has(b)) m.set(b, []);
      m.get(b)!.push(s);
    }
    return m;
  }, [suggestions]);

  // État déplié par bucket (tous ouverts par défaut)
  const [open, setOpen] = useState<Record<Bucket, boolean>>(() => {
    const init = {} as Record<Bucket, boolean>;
    BUCKET_ORDER.forEach((b) => (init[b] = true));
    return init;
  });

  if (suggestions.length === 0) {
    return (
      <div className="card-meta" style={{ fontSize: 12, padding: 14 }}>
        Aucune donnée chiffrée exploitable détectée dans les extractions IA.
      </div>
    );
  }

  return (
    <div className="chart-suggestions">
      <div className="chart-sugg-head">
        <strong style={{ fontSize: 14 }}>📈 Visualisations disponibles</strong>
        <span className="card-meta" style={{ fontSize: 11 }}>
          {suggestions.length} graphique{suggestions.length > 1 ? 's' : ''} détecté
          {suggestions.length > 1 ? 's' : ''} dans tes documents — clique pour ajouter
        </span>
      </div>

      {BUCKET_ORDER.map((bucket) => {
        const items = grouped.get(bucket);
        if (!items || items.length === 0) return null;
        const meta = BUCKET_META[bucket];
        const isOpen = open[bucket];
        return (
          <div key={bucket} className="chart-sugg-group">
            <button
              type="button"
              className={`chart-sugg-group-head ${isOpen ? 'open' : ''}`}
              onClick={() => setOpen((o) => ({ ...o, [bucket]: !o[bucket] }))}
              aria-expanded={isOpen}
            >
              <span className="chevron">{isOpen ? '▾' : '▸'}</span>
              <span className="chart-sugg-group-icon">{meta.icon}</span>
              <span className="chart-sugg-group-label">{meta.label.toUpperCase()}</span>
              <span className="chart-sugg-group-count">{items.length}</span>
              <span className="chart-sugg-group-hint">{meta.hint}</span>
            </button>
            {isOpen && (
              <div className="chart-sugg-grid">
                {items.map((s) => (
                  <SuggestionTile key={s.id} suggestion={s} onAdd={() => onAdd(s)} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SuggestionTile({
  suggestion,
  onAdd,
}: {
  suggestion: ChartSuggestion;
  onAdd: () => void;
}) {
  return (
    <button className="chart-sugg-tile" onClick={onAdd} type="button">
      <div className="chart-sugg-preview">
        <MiniPreview s={suggestion} />
      </div>
      <div className="chart-sugg-info">
        <div className="chart-sugg-title">{suggestion.title}</div>
        <div className="chart-sugg-meta">
          {KIND_LABELS[suggestion.kind]} · {suggestion.description}
        </div>
        <div className="chart-sugg-source">📄 {suggestion.source}</div>
      </div>
      <div className="chart-sugg-add">＋</div>
    </button>
  );
}

/** Mini-aperçu compact pour la tuile. */
function MiniPreview({ s }: { s: ChartSuggestion }) {
  if (s.kind === 'donut') {
    return (
      <div style={{ pointerEvents: 'none' }}>
        <Donut data={s.data.slice(0, 6)} size={92} thickness={22} centerLabel="" centerValue="" legend="none" />
      </div>
    );
  }
  if (s.kind === 'bar') {
    return (
      <div style={{ width: 140, height: 88, pointerEvents: 'none', overflow: 'hidden' }}>
        <BarChart
          data={s.data.slice(0, 6).map((d: any) => ({ label: d.label, value: d.value }))}
          series={[{ key: 'value', label: '' }]}
          height={88}
        />
      </div>
    );
  }
  if (s.kind === 'timeline') {
    const items = s.timeline_items ?? [];
    return (
      <div className="mini-timeline">
        {items.slice(0, 3).map((it, i) => (
          <div key={i} className="mini-tl-item">
            <div className="mini-tl-dot" />
            <div className="mini-tl-text">
              <div className="mini-tl-date">{it.date.slice(0, 10)}</div>
              <div className="mini-tl-title">{it.title.slice(0, 24)}</div>
            </div>
          </div>
        ))}
        {items.length > 3 && <div className="mini-tl-more">+ {items.length - 3}</div>}
      </div>
    );
  }
  if (s.kind === 'table') {
    const cols = s.table_columns ?? [];
    const rows = s.table_rows ?? [];
    return (
      <div className="mini-table-wrap">
        <table className="mini-table">
          <thead>
            <tr>
              {cols.slice(0, 3).map((c, i) => (
                <th key={i}>{c.slice(0, 10)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 3).map((r, i) => (
              <tr key={i}>
                {r.slice(0, 3).map((c, j) => (
                  <td key={j}>{String(c).slice(0, 12)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 3 && <div className="mini-table-more">+ {rows.length - 3} lignes</div>}
      </div>
    );
  }
  return <div className="mini-fallback">{KIND_LABELS[s.kind]}</div>;
}
